import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tipApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, Lightbulb, ExternalLink, Clock, Target, User } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Tip } from '@/types/learningManagement';
import type { Skill } from '@/types/skillKnowledge';

export const TipDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [skill, setSkill] = useState<Skill | null>(null);

  const canEdit = hasPermission('edit', 'tip');
  const canDelete = hasPermission('delete', 'tip');

  // Use entity hook for fetching tip
  const {
    data: tip,
    isLoading,
    error,
    refetch,
  } = useEntity<Tip>({
    fetchFn: (tipId: string) => tipApi.getById(tipId),
    entityId: id,
    entityName: 'tip',
    enabled: !!id,
  });

  // Fetch skill details if skillId exists
  useEffect(() => {
    if (!tip?.skillId) {
      setSkill(null);
      return;
    }

    skillApi.getById(tip.skillId)
      .then(setSkill)
      .catch((err) => {
        console.error('Error loading skill:', err);
        setSkill(null);
      });
  }, [tip?.skillId]);

  const isSystemDefined = tip?.tipGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'tip'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Tip ID is required');
      await tipApi.delete(id);
    },
    entityName: 'tip',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/tips';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this tip?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading tip..." />;
  }

  if (error || !tip) {
    return <ErrorState error={error || new Error('Tip not found')} onRetry={refetch} />;
  }

  const tipId = tip.tipId || tip.id || '';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'BEGINNER': case 'EASY': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'INTERMEDIATE': case 'MODERATE': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ADVANCED': case 'CHALLENGING': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'EXPERT': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/tips">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Lightbulb className="h-6 w-6 text-yellow-500" />
              <h1 className="text-3xl font-bold tracking-tight">{tip.title}</h1>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tip.status)}`}>
                {tip.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(tip.difficultyLevel)}`}>
                {tip.difficultyLevel}
              </span>
              {isSystemDefined && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  System Defined
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/tips/${tipId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core tip details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={tipId} label="Tip ID" />
            {tip.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="mt-1 text-sm">{tip.description}</p>
              </div>
            )}
            {tip.summary && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Summary</label>
                <p className="mt-1 text-sm">{tip.summary}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content</label>
              <p className="mt-1 text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">{tip.content}</p>
            </div>
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader>
            <CardTitle>Classification</CardTitle>
            <CardDescription>Category and type information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                    {tip.category.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tip Type</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm">
                    {tip.tipType.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            </div>
            {tip.subCategory && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sub Category</label>
                <p className="mt-1 text-sm">{tip.subCategory}</p>
              </div>
            )}
            {tip.implementationTimeMinutes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Implementation Time</label>
                <p className="mt-1 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{tip.implementationTimeMinutes} minutes</span>
                </p>
              </div>
            )}
            {tip.impactLevel && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Impact Level</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded text-sm">
                    {tip.impactLevel}
                  </span>
                </p>
              </div>
            )}
            {tip.targetAudience && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Target Audience</label>
                <p className="mt-1 flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{tip.targetAudience}</span>
                </p>
              </div>
            )}
            {tip.relevanceScore !== undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Relevance Score</label>
                <p className="mt-1 text-sm font-medium">{tip.relevanceScore}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Related Skill */}
        {tip.skillId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Related Skill
              </CardTitle>
              <CardDescription>Skill associated with this tip</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/admin/skills/${tip.skillId}`}
                    className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                  >
                    {skill?.name || 'View Skill'}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
                <CopyableId id={tip.skillId} label="Skill ID" />
                {skill?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                )}
                {skill?.category && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                    {skill.category.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Author Information */}
        {tip.authorInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Author Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {tip.authorInfo.authorName && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="mt-1 text-sm">{tip.authorInfo.authorName}</p>
                  </div>
                )}
                {tip.authorInfo.authorEmail && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="mt-1 text-sm">{tip.authorInfo.authorEmail}</p>
                  </div>
                )}
                {tip.authorInfo.authorId && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Author ID</label>
                    <p className="mt-1 font-mono text-sm">{tip.authorInfo.authorId}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {tip.createdAt ? new Date(tip.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {tip.updatedAt ? new Date(tip.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {tip.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{tip.createdBy}</p>
                </div>
              )}
              {tip.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{tip.updatedBy}</p>
                </div>
              )}
              {tip.version !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="mt-1 text-sm">{tip.version}</p>
                </div>
              )}
              {tip.tipGenerated && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Generated</label>
                  <p className="mt-1 text-sm">{tip.tipGenerated.replace(/_/g, ' ')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Associated Nudges */}
      {tip.associatedNudges && tip.associatedNudges.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Associated Nudges ({tip.associatedNudges.length})</CardTitle>
            <CardDescription>Nudges linked to this tip</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tip.associatedNudges.map((nudgeId, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <Link
                    to={`/admin/nudges/${nudgeId}`}
                    className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                  >
                    {nudgeId}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resources */}
      {tip.resources && tip.resources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resources ({tip.resources.length})</CardTitle>
            <CardDescription>Additional resources for this tip</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tip.resources.map((resource, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 text-sm"
                >
                  {resource}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {tip.tags && tip.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags ({tip.tags.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tip.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TipDetailPage;
